import React, { useState, useEffect } from 'react';
import { Clock, X, Send, Trash2, Pencil, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ScheduleMessageModal = ({ chatId, token, onClose, onScheduled }) => {
  const [content, setContent] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [existingScheduled, setExistingScheduled] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('new');
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');

  useEffect(() => { fetchScheduled(); }, []);

  const fetchScheduled = async () => {
    try {
      const res = await axios.get(`${API}/messages/scheduled`, { headers: { Authorization: `Bearer ${token}` } });
      setExistingScheduled(res.data.filter(m => m.chat_id === chatId));
    } catch {}
  };

  const handleSchedule = async () => {
    if (!content.trim() || !scheduledDate || !scheduledTime) {
      toast.error('Please fill in message and date/time');
      return;
    }
    const sendAt = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
    if (new Date(sendAt) <= new Date()) {
      toast.error('Scheduled time must be in the future');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/messages/schedule`, { chat_id: chatId, content: content.trim(), send_at: sendAt }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Message scheduled!');
      setContent(''); setScheduledDate(''); setScheduledTime('');
      fetchScheduled();
      if (onScheduled) onScheduled();
    } catch { toast.error('Failed to schedule message'); }
    finally { setLoading(false); }
  };

  const cancelScheduled = async (id) => {
    try {
      await axios.delete(`${API}/messages/schedule/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Cancelled');
      fetchScheduled();
    } catch { toast.error('Failed to cancel'); }
  };

  const startEdit = (msg) => {
    setEditingId(msg.id);
    setEditContent(msg.content);
    const d = new Date(msg.send_at);
    setEditDate(d.toISOString().split('T')[0]);
    setEditTime(d.toTimeString().slice(0, 5));
  };

  const saveEdit = async () => {
    if (!editContent.trim() || !editDate || !editTime) {
      toast.error('Fill in all fields');
      return;
    }
    const sendAt = new Date(`${editDate}T${editTime}`).toISOString();
    if (new Date(sendAt) <= new Date()) {
      toast.error('Time must be in the future');
      return;
    }
    try {
      await axios.put(`${API}/messages/schedule/${editingId}`, { content: editContent.trim(), send_at: sendAt }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Updated!');
      setEditingId(null);
      fetchScheduled();
    } catch { toast.error('Failed to update'); }
  };

  const formatDateTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="schedule-message-modal">
      <div className="bg-white rounded-3xl max-w-md w-full mx-4 overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-[#D4AF37]/10 to-transparent">
          <div className="flex items-center gap-2">
            <Clock size={20} className="text-[#D4AF37]" />
            <h2 className="font-medium text-lg">Schedule Message</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="close-schedule-modal">
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button onClick={() => setTab('new')} className={`flex-1 py-3 text-sm font-medium transition-colors ${tab === 'new' ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-gray-500'}`} data-testid="tab-new-schedule">New</button>
          <button onClick={() => setTab('existing')} className={`flex-1 py-3 text-sm font-medium transition-colors ${tab === 'existing' ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-gray-500'}`} data-testid="tab-existing-schedule">Scheduled ({existingScheduled.length})</button>
        </div>

        <div className="p-4 max-h-[400px] overflow-y-auto">
          {tab === 'new' ? (
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Message</label>
                <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Type your message..." rows={3} className="w-full border rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] outline-none resize-none" data-testid="schedule-message-input" />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-sm text-gray-600 mb-1 block">Date</label>
                  <input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} min={today} className="w-full border rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] outline-none" data-testid="schedule-date-input" />
                </div>
                <div className="flex-1">
                  <label className="text-sm text-gray-600 mb-1 block">Time</label>
                  <input type="time" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} className="w-full border rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] outline-none" data-testid="schedule-time-input" />
                </div>
              </div>
              <button onClick={handleSchedule} disabled={loading || !content.trim() || !scheduledDate || !scheduledTime} className="w-full py-3 rounded-xl bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white font-medium flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50 transition-all" data-testid="confirm-schedule-btn">
                <Send size={16} />
                {loading ? 'Scheduling...' : 'Schedule Message'}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {existingScheduled.length === 0 ? (
                <p className="text-center text-gray-400 py-8 text-sm">No scheduled messages for this chat</p>
              ) : (
                existingScheduled.map((msg) => (
                  <div key={msg.id} className="p-3 bg-gray-50 rounded-xl" data-testid={`scheduled-msg-${msg.id}`}>
                    {editingId === msg.id ? (
                      <div className="space-y-2">
                        <textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} rows={2} className="w-full border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#D4AF37] outline-none resize-none" />
                        <div className="flex gap-2">
                          <input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} min={today} className="flex-1 border rounded-lg px-2 py-1.5 text-sm" />
                          <input type="time" value={editTime} onChange={(e) => setEditTime(e.target.value)} className="flex-1 border rounded-lg px-2 py-1.5 text-sm" />
                        </div>
                        <div className="flex gap-2">
                          <button onClick={saveEdit} className="flex-1 py-1.5 rounded-lg bg-[#D4AF37] text-white text-sm flex items-center justify-center gap-1" data-testid="save-edit-schedule">
                            <Check size={14} /> Save
                          </button>
                          <button onClick={() => setEditingId(null)} className="flex-1 py-1.5 rounded-lg bg-gray-200 text-gray-700 text-sm">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-3">
                        <Clock size={16} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-800 line-clamp-2">{msg.content}</p>
                          <p className="text-xs text-gray-500 mt-1">{formatDateTime(msg.send_at)}</p>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <button onClick={() => startEdit(msg)} className="w-7 h-7 rounded-full hover:bg-gray-200 flex items-center justify-center" data-testid={`edit-scheduled-${msg.id}`}>
                            <Pencil size={13} className="text-gray-500" />
                          </button>
                          <button onClick={() => cancelScheduled(msg.id)} className="w-7 h-7 rounded-full hover:bg-red-50 flex items-center justify-center" data-testid={`cancel-scheduled-${msg.id}`}>
                            <Trash2 size={13} className="text-red-500" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleMessageModal;
